<template>
  <div class="pa-4">
    <v-card flat>
      <v-card-text>
        <v-row>
          <v-col cols="10">
            <v-select v-show="!customPage || !newPage ? true : false" @input="getPageData()" v-model="selectedPageId"
              :items="pages" item-text="name" item-value="_id" :label="$t('message.available_pages')" outlined
              dense></v-select>
          </v-col>
          <v-col cols="2" class="text-right">
            <v-btn v-show="!customPage || !newPage ? true : false" color="primary" outlined @click="startNew()">{{
              $t('message.add') }}</v-btn>
            <v-btn elevation="0" color="error" v-show="newPage ? true : false" outlined
              @click="newPage = false; customPage = null">{{
                $t('message.cancel') }}</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
      <div v-if="loading">
        <v-skeleton-loader type="card ,article, actions"></v-skeleton-loader>
      </div>
      <v-card-text v-else-if="customPage">
        <div v-if="customPage.user && customPage.user._id">
          <v-list-item class="mb-2" @click="goToMember(customPage.user._id, customPage.user.displayName)">
            <v-list-item-avatar>
              <ImageDisplay v-if="customPage.user.userImageSmall" :src="customPage.user.userImageSmall" />
              <img v-else :src="assets('default-profile-avatar.png')" />
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>
                {{ customPage.user.displayName }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ $t('message.created_by') }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </div>
        <v-select @input="updateTypeOptions()" v-model="customPage.type" :items="types" item-text="text"
          item-value="value" :label="$t('message.page_type')" outlined dense></v-select>
        <v-text-field outlined dense :label="$t('message.name')" v-model="customPage.name"></v-text-field>
        <v-select small-chips hide-details outlined multiple :label="$t('message.items')" dense
          :items="availableTypeItems" v-model="customPage.items" item-text="text" item-value="_id"></v-select>
        <v-switch hide-details v-model="customPage.active" :label="$t('message.active')"></v-switch>
        <v-switch hide-details v-model="customPage.embed" :label="$t('message.remove_logos')"></v-switch>
        <div class="text-caption mb-2">{{ $t('message.hide_logos_explained') }}</div>
        <v-switch v-model="customPage.transparentBackground" :label="$t('message.transparent_background')"></v-switch>
        <v-select dense outlined v-model="customPage.theme" :label="$t('message.theme')"
          :items="availableThemes"></v-select>
        <v-switch v-model="showHtmlEditor" :label="showHtmlEditor ? 'HTML' : $t('message.custom_text')"></v-switch>
        <VueEditor v-if="!showHtmlEditor" :style="'background-color:white;color:black'" v-model="customPage.html"
          :editor-toolbar="editorToolbar" />
        <v-textarea v-else style="font-family: 'Courier New', monospace;" hide-details outlined dense rows="6"
          v-model="customPage.html"></v-textarea>
        <div class="text-caption mb-2">({{ $t('message.optional') }})</div>
        <v-textarea style="font-family: 'Courier New', monospace;" @blur="ensureScriptTags" class="mt-2" hide-details
          outlined dense :label="$t('message.custom_head_code')" v-if="!newPage" rows="3"
          v-model="customPage.customHeadCode"></v-textarea>
        <div v-if="!newPage" class="text-caption">{{ $t('message.custom_head_code_explained') }}</div>
      </v-card-text>
      <v-card-actions v-if="customPage && !newPage">
        <v-btn icon @click="openQR()" :title="$t('message.qr_code')"><v-icon>mdi-qrcode</v-icon></v-btn>
        <v-btn class="ml-1" icon @click="copyLink()" :title="$t('message.copy_link')"><v-icon>mdi-content-copy</v-icon></v-btn>
        <v-btn class="ml-1" outlined small @click="copyEmbedCode()">
          <v-icon left small>mdi-code-tags</v-icon>
          {{ $t('message.copy_embed_code') }}
        </v-btn>
      </v-card-actions>
      <v-card-actions v-if="customPage">
        <v-btn block @click="addNewPage" v-if="newPage" outlined color="primary">{{ $t('message.add') }}</v-btn>
        <v-btn block @click="updatePage" v-else outlined color="primary">{{ $t('message.update') }}</v-btn>
      </v-card-actions>
      <v-expansion-panels v-if="customPage && !newPage" class="mt-2" flat v-model="panels">
        <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'">
          <v-expansion-panel-header>
            <div class="text-left" @click="loadActivityGraph()">
              <v-icon>mdi-chart-bell-curve</v-icon>
              {{ $t("message.activity_graph") }}
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <GraphLoader type="line" v-if="loadingActivityGraph"></GraphLoader>
            <div v-else-if="activityGraph.hasData">
              <ApexChart style="margin-top:-30px" type="line" height="200" :options="activityGraph.options"
                :series="activityGraph.series">
              </ApexChart>
            </div>
            <div v-else>{{ $t("message.no_data") }}</div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>
    <v-dialog v-model="viewQRDialog">
      <v-card outlined v-if="currentQRCode">
        <v-card-title>
          {{ customPage.name }}
        </v-card-title>
        <v-card-text class="text-center">
          <div style="display: inline-block;padding: 5px; border: 2px solid darkslategrey; border-radius: 5px;">
            <vue-qr v-if="currentQRCode.url" :text="currentQRCode.url" :size="300"></vue-qr>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn elevation="0" @click="viewQRDialog = false;">
            {{ $t('message.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import GraphLoader from "@/components/displays/GraphLoader.vue";
import Api from "@/services/Api";
import { Environment } from "@/services/Environment";
import ApexChart from 'vue-apexcharts';
import VueQr from 'vue-qrcode-component';
import { VueEditor } from "vue2-editor";

export default {
  data() {
    return {
      pageId: this.$route.query.pageId,
      addingNew: false,
      selectedPageId: null,
      loading: false,
      customPage: null,
      newPage: false,
      availableTypeItems: [],
      availableThemes: [{
        text: this.$t('message.light'),
        value: 'light'
      },
      {
        text: this.$t('message.dark'),
        value: 'dark'
      }],
      viewQRDialog: null,
      panels: null,
      currentQRCode: null,
      activityGraph: { ready: false, hasData: false, series: [], options: null },
      loadingActivityGraph: true,
      pages: [],
      types: [
        {
          text: this.$t('message.class_booking'),
          value: 'class-booking'
        },
        {
          text: this.$t('message.schedule'),
          value: 'class-schedule'
        }],
      typeOptions: [],
      showHtmlEditor: false,
      activity: [],
      editorToolbar: this.getEditorToolbar(),
      paymentsEnabled: false
    };
  },
  created() {
    Api()
      .get("admins/academy/" + this.$store.state.selectedAcademy)
      .then((response) => {
        if (response.data.stripeAccountId)
          this.paymentsEnabled = true;
        if (this.paymentsEnabled)
          this.types.push({
            text: this.$t('message.public_memberships_page'),
            value: 'pmp'
          })
        this.getPages()
      });
  },
  components: {
    VueEditor,
    VueQr,
    GraphLoader,
    ApexChart
  },
  methods: {
    loadActivityGraph() {
      this.loadingActivityGraph = true;
      Api()
        .get("pages/managedacademy/" + this.$store.state.managedAcademy + "/page/" + this.customPage._id + "/activity", this.customPage)
        .then((response) => {
          if (response.data.days.lenght == 0) {
            this.loadingActivityGraph = false;
            return;
          }
          // 1. Format your dates into simple strings first
          const categoryLabels = response.data.days.map(day => {
            const d = new Date(day);
            // Returns "23 Feb"
            return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
          });

          this.activityGraph = {
            series: [{
              name: this.$t('message.activity'),
              data: response.data.data // [13, 35]
            }],
            ready: true,
            hasData: true,
            options: {
              chart: {
                type: 'line',
                toolbar: { show: false },
                background: 'transparent'
              },
              xaxis: {
                type: 'category', // CHANGE THIS from 'datetime'
                categories: categoryLabels, // Now using ["23 Feb", "24 Feb"]
                labels: {
                  show: true,
                  rotate: -45,
                  // This ensures that if you have 90 days, it hides overlapping text
                  hideOverlappingLabels: true,
                },
                tooltip: { enabled: false }
              },
              colors: [this.themeColor('success')],
              stroke: { curve: 'smooth', width: 3 },
              grid: { show: false },
              yaxis: {
                labels: { show: true }
              },
              theme: {
                mode: this.isDarkMode() ? 'dark' : 'light',
              }
            }
          }
          this.loadingActivityGraph = false;
        }).catch(() => {
          this.loadingActivityGraph = false;
        });
    },
    ensureScriptTags() {
      var code = this.customPage.customHeadCode;
      this.validationError = "";

      if (!code || code.trim() === "") {
        this.customPage.customHeadCode = "";
        return;
      }

      // 1. Initial cleanup
      code = code.trim();

      // 2. Define fragments to keep your VS Code formatter happy
      var sOpen = "<scr" + "ipt>";
      var sClose = "</scr" + "ipt>";

      // 3. Regex to peel off any number of existing tags at the start/end
      // This handles <script><script> or accidental double closings
      var openRegex = new RegExp("^(" + sOpen + ")+", "gi");
      var closeRegex = new RegExp("(" + sClose + ")+$", "gi");

      // Remove the tags so we are left with only the "meat" of the script
      var cleanedContent = code.replace(openRegex, "").replace(closeRegex, "").trim();

      // 4. Re-wrap with exactly ONE set of tags
      code = sOpen + "\n" + cleanedContent + "\n" + sClose;

      // 5. Final Empty Content Check
      if (cleanedContent.length === 0) {
        this.validationError = "Error: The script appears to be empty.";
        this.customPage.customHeadCode = ""; // Don't save just empty tags
        return;
      }

      // 6. Syntax Sanity Check (Brackets)
      var openBrace = (code.match(/{/g) || []).length;
      var closeBrace = (code.match(/}/g) || []).length;
      var openParen = (code.match(/\(/g) || []).length;
      var closeParen = (code.match(/\)/g) || []).length;

      if (openBrace !== closeBrace || openParen !== closeParen) {
        this.validationError = "Warning: Mismatched brackets detected.";
      }

      this.customPage.customHeadCode = code;
    },
    openQR() {
      const baseUrl = Environment.get("VUE_APP_API_HOST");
      const urlToCopy = baseUrl + '/pub/academy/p/' + this.selectedPageId;
      this.currentQRCode = { title: this.customPage.name, url: urlToCopy, type: 'custom-page' };
      this.viewQRDialog = true;
    },
    copyLink() {
      const baseUrl = Environment.get("VUE_APP_API_HOST");
      const urlToCopy = baseUrl + '/pub/academy/p/' + this.selectedPageId;
      this.copyToClipboard(urlToCopy);
    },
    copyEmbedCode() {
      const baseUrl = Environment.get("VUE_APP_API_HOST");
      let url = baseUrl + '/pub/academy/p/' + this.selectedPageId;
      const params = [];
      if (this.customPage.embed) params.push('embed=true');
      if (this.customPage.transparentBackground) params.push('tbg=true');
      if (this.customPage.theme) params.push('theme=' + this.customPage.theme);
      if (params.length) url += '?' + params.join('&');
      const iframe = '<iframe style="width:100%;height:600px;border:0" frameBorder="0" src="' + url + '"></iframe>';
      try {
        navigator.clipboard.writeText(iframe);
        this.showAlert("success", this.$t("message.copied"));
      } catch (err) {
        this.showAlert("error", this.$t("message.error_generic"));
      }
    },
    startNew() {
      this.selectedPageId = null;
      this.newPage = true;
      this.customPage = {
        user: this.$store.state.user._id,
        managedAcademy: this.$store.state.managedAcademy,
        type: null,
        active: false,
        embed: false,
        items: [],
        name: '',
        html: '',
        newPage: null,
      };
    },
    getPages() {
      Api()
        .get("pages/managedacademy/" + this.$store.state.managedAcademy + "/pages/all")
        .then((response) => {
          if (response.data.pages) {
            this.pages = response.data.pages;
          }
        });
    },
    getPageData() {
      this.loading = true;
      this.newPage = false;
      this.activityGraph = { ready: false, hasData: false, series: [], options: null };
      this.loadingActivityGraph = true;
      Api()
        .get("pages/managedacademy/" + this.$store.state.managedAcademy + "/page/" + this.selectedPageId)
        .then((response) => {
          if (response.data.page) {
            this.customPage = response.data.page;
            this.updateTypeOptions();
            this.loading = false;
          }
        });
    },
    updateTypeOptions() {
      //Get differnt Ids depending on the type
      Api()
        .get("pages/managedacademy/" + this.$store.state.managedAcademy + "/type/" + this.customPage.type + "/items")
        .then((response) => {
          if (response.data.list) {
            this.availableTypeItems = response.data.list;
          }
        });
    },
    canAdd() {
      if (this.customPage && this.customPage.items && this.customPage.items.length && this.customPage.name &&
        this.customPage.type) {
        return true;
      }
      this.showAlert("error", this.$t("message.fill_all_params"));
      return false;
    },
    updatePage() {
      Api()
        .patch("pages/managedacademy/" + this.$store.state.managedAcademy + "/page/" + this.customPage._id, this.customPage)
        .then(() => {
          this.showAlert("success", this.$t("message.updated"));
        }).catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    addNewPage() {
      Api()
        .post("pages/managedacademy/" + this.$store.state.managedAcademy + "/page", this.customPage)
        .then((response) => {
          this.selectedPageId = response.data.pageId;
          this.customPage._id = response.data.pageId;
          this.newPage = false;
          this.getPages();
          this.showAlert("success", this.$t("message.success"));
        }).catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    getActivityGraph() {
      if (!this.activityGraph.ready) {
        Api()
          .get("pages/managedacademy/" + this.$store.state.managedAcademy + "/page/" + this.customPage._id + "/activity")
          .then((response) => {
            if (response.data.data) {
              //build graoh
              console.log(response.data.data)
            }
          });
      }
    }
  }
};
</script>

<style scoped>
.video-thumbnail {
  width: 80px
}
</style>